/**
 * Localization framework (browser-safe core). Catalog loading for Node lives in
 * `@connaro/i18n/node`; the web app imports catalog JSON directly via its bundler.
 */

export const LOCALES = ['en-US', 'es-PR'] as const;
export type Locale = (typeof LOCALES)[number];
export const DEFAULT_LOCALE: Locale = 'en-US';

export function isLocale(value: string): value is Locale {
  return (LOCALES as readonly string[]).includes(value);
}

/** A loaded, possibly nested message catalog. */
export type Catalog = { [key: string]: string | Catalog };

/** Flatten a nested catalog to dotted keys (e.g. `auth.password.tooShort`). */
export function flatten(
  catalog: Catalog,
  prefix = '',
  out: Record<string, string> = {},
): Record<string, string> {
  for (const [key, value] of Object.entries(catalog)) {
    const full = prefix ? `${prefix}.${key}` : key;
    if (value !== null && typeof value === 'object') flatten(value, full, out);
    else out[full] = value;
  }
  return out;
}

/**
 * Resolve a dotted key against a catalog, interpolating `{name}` placeholders.
 * Falls back to the key itself if missing (so a missing string is visible, not blank).
 */
export function translate(
  catalog: Catalog,
  key: string,
  params?: Record<string, string | number>,
): string {
  const parts = key.split('.');
  let node: string | Catalog | undefined = catalog;
  for (const part of parts) {
    if (node === null || typeof node !== 'object') {
      node = undefined;
      break;
    }
    node = node[part];
  }
  if (typeof node !== 'string') return key;
  if (!params) return node;
  return node.replace(/\{(\w+)\}/g, (_, name: string) =>
    name in params ? String(params[name]) : `{${name}}`,
  );
}

/** Pick the best supported locale from an Accept-Language header. */
export function negotiateLocale(acceptLanguage: string | undefined | null): Locale {
  if (!acceptLanguage) return DEFAULT_LOCALE;
  const requested = acceptLanguage
    .split(',')
    .map((part) => {
      const [tag, q] = part.trim().split(';q=');
      return { tag: (tag ?? '').trim(), q: q ? Number(q) : 1 };
    })
    .sort((a, b) => b.q - a.q);
  for (const { tag } of requested) {
    if (isLocale(tag)) return tag;
    // Language-only match, e.g. "es" -> "es-PR".
    const lang = tag.split('-')[0];
    const match = LOCALES.find((l) => l.split('-')[0] === lang);
    if (match) return match;
  }
  return DEFAULT_LOCALE;
}
